import React from 'react';
import { Button, Text, Box, VisuallyHidden } from '@chakra-ui/react';
import * as Icons from 'react-icons/fa6';

const renderIcons = (icons) => {
  if (!icons || icons.length === 0) return null;

  return icons.map((icon, index) => {
    const IconComponent = icon.name ? Icons[icon.name] : null;

    if (!IconComponent) {
      console.warn(`Icon "${icon.name}" not found.`);
      return null;
    }

    const variantStyles = icon.variant
      ? {
          size: `var(--icon-${icon.variant}-size, 36px)`,
          color: `var(--icon-${icon.variant}-color, var(--vibrant-purple))`,
        }
      : {};

    return (
      <IconComponent
        key={index}
        style={{
          position: icon.position ? 'absolute' : 'relative',
          top: icon.position?.top || '0px',
          left: icon.position?.left || '0px',
          width: icon.size || variantStyles.size,
          height: icon.size || variantStyles.size,
          color: icon.color || variantStyles.color,
        }}
        aria-hidden="true"
      />
    );
  });
};

const ButtonComponent = ({
  label,
  variant = 'solid',
  textVariant = 'cta-text-lg',
  isDisabled = false,
  isLoading = false,
  onClick,
  marginBottom,
  icons = [],
  textDecoration,
  isTouchDevice,
  isMouseDevice,
  style = {}
}) => {
  const makeDisabled = label?.toLowerCase() === 'back' ? false : isDisabled;
  
  const effectiveVariant = makeDisabled ? 'disabled' : variant;
  const effectiveTextVariant = ['select-all', 'deselect-all'].includes(variant) ? 'cta-text-md' : textVariant;
  
  const buttonProps = {
    width: `var(--button-${variant}-width, 146px)`,
    height: `var(--button-${variant}-height, 24px)`,
    padding: `var(--button-${variant}-padding)`,
    gap: `var(--button-${variant}-gap, 8px)`,
    borderRadius: `var(--button-${variant}-border-radius, 24px)`,
    border: `var(--button-${variant}-border, 2px solid var(--button-${variant}-border-color, transparent))`,
    borderColor: `var(--button-${variant}-border-color)`,
    backgroundColor: makeDisabled ? `var(--button-disabled-bg, var(--button-${variant}-bg))` : `var(--button-${variant}-bg)`,
    color: makeDisabled ? `var(--button-disabled-color, var(--button-${variant}-color))` : `var(--button-${variant}-color)`,
    opacity: makeDisabled ? `var(--button-disabled-opacity, var(--button-${variant}-opacity, 1))` : `var(--button-${variant}-opacity, 1)`,
    display: 
      ['outline-half', 'solid-half', 'disabled-half', 'select-all', 'deselect-all'].includes(variant)
        ? 'inline-block'
        : 'flex',
    justifyContent: 
      variant === 'modal'
        ? 'flex-start'
        : variant === 'container'
        ? 'space-between'
        : 'center',
    alignItems: "center",
    marginTop: (() => {
      // Add extra margin-top for disabled "Add" buttons
      const isAddButton = label?.toLowerCase().includes('add');
      const baseMargin = `var(--button-${variant}-margin-top)`;
      
      if (makeDisabled && isAddButton) {
        return '24px'; // Override with 24px margin-top for disabled Add buttons
      }
      
      return baseMargin;
    })(),
    marginBottom: ['select-all', 'deselect-all'].includes(variant) ? '0' : (marginBottom || '24px'),
    marginLeft: variant === 'modal' ? 'auto' : 'none',
    float:
      variant === 'modal'
        ? 'right'
        : ['solid-half', 'disabled-half', 'select-all', 'deselect-all'].includes(variant)
        ? 'right'
        : 'none',
    maxWidth: variant === 'modal' ? 'fit-content' : 'unset',
    minHeight:  `var(--button-${variant}-min-height, 45px)`,
    cursor: makeDisabled ? 'not-allowed' : 'pointer',
    isDisabled: makeDisabled,
    isLoading: isLoading,
    onClick: onClick,
    'aria-label': label || 'Button',
    role: "button",
    'aria-busy': isLoading,
    tabIndex: makeDisabled ? -1 : 0,
    ...style,
  };
  
  // Variant-specific state overrides
  if (variant === 'select-all') {
    buttonProps._hover = { backgroundColor: 'var(--button-select-all-hover-bg-color, #F1E0FF)', color: 'var(--button-select-all-hover-text-color, #000000)' };
    buttonProps._focus = { backgroundColor: 'var(--button-select-all-focus-bg-color, #FFFFFF)', color: 'var(--button-select-all-focus-text-color, #000000)' };
    buttonProps._active = { backgroundColor: 'var(--button-select-all-active-bg-color, #D9B3FF)', color: 'var(--button-select-all-active-text-color, #000000)' };
  }
  if (variant === 'deselect-all') {
    buttonProps._hover = { backgroundColor: 'var(--button-deselect-all-hover-bg-color, #F1E0FF)', color: 'var(--button-deselect-all-hover-text-color, #000000)' };
    buttonProps._focus = { backgroundColor: 'var(--button-deselect-all-focus-bg-color, #F1E0FF)', color: 'var(--button-deselect-all-focus-text-color, #000000)' };
    buttonProps._active = { backgroundColor: 'var(--button-deselect-all-active-bg-color, #D9B3FF)', color: 'var(--button-deselect-all-active-text-color, #000000)' };
  }

  buttonProps._disabled = {
    bg: `var(--button-disabled-bg, var(--button-${variant}-bg))`,
    color: `var(--button-disabled-color, var(--button-${variant}-color))`,
    opacity: `var(--button-disabled-opacity, var(--button-${variant}-opacity))`,
    cursor: 'not-allowed',
    boxShadow: 'none',
    pointerEvents: 'none',
    borderColor: `var(--button-disabled-border-color, var(--button-${variant}-border-color))`,
  };
  
  if (!isTouchDevice) {
    buttonProps.sx = {
      '&:hover': {
        backgroundColor: makeDisabled 
          ? `var(--button-disabled-hover-bg-color, var(--button-disabled-bg))` 
          : `var(--button-${variant}-hover-bg-color)`,
        color: makeDisabled 
          ? `var(--button-disabled-hover-text-color, var(--button-disabled-color))` 
          : `var(--button-${variant}-hover-text-color)`,
        borderColor: makeDisabled 
          ? `var(--button-disabled-hover-border-color, var(--button-disabled-border-color))` 
          : `var(--button-${variant}-hover-border-color)`,
      },
      '&:active': {
        backgroundColor: makeDisabled 
          ? `var(--button-disabled-active-bg-color, var(--button-disabled-bg))` 
          : `var(--button-${variant}-active-bg-color)`,
        color: makeDisabled 
          ? `var(--button-disabled-active-text-color, var(--button-disabled-color))` 
          : `var(--button-${variant}-active-text-color)`,
        borderColor: makeDisabled 
          ? `var(--button-disabled-active-border-color, var(--button-disabled-border-color))` 
          : `var(--button-${variant}-active-border-color)`,
      },
      '&:focus': {
        backgroundColor: makeDisabled 
          ? `var(--button-disabled-focus-bg-color, var(--button-disabled-bg))` 
          : `var(--button-${variant}-focus-bg-color)`,
        color: makeDisabled 
          ? `var(--button-disabled-focus-text-color, var(--button-disabled-color))` 
          : `var(--button-${variant}-focus-text-color)`,
        borderColor: makeDisabled 
          ? `var(--button-disabled-focus-border-color, var(--button-disabled-border-color))` 
          : `var(--button-${variant}-focus-border-color)`,
      }
    };
  }
  
  if (isTouchDevice) {
    buttonProps.style = {
      ...buttonProps.style,
      WebkitTapHighlightColor: 'transparent',
      WebkitTouchCallout: 'none',
      WebkitUserSelect: 'none',
      WebkitAppearance: 'none',
      touchAction: 'manipulation',
    };
    
    buttonProps._active = { 
      bg: makeDisabled ? `var(--button-disabled-bg)` : `var(--button-${variant}-bg)`, 
      transform: 'none' 
    };
    buttonProps._pressed = { 
      bg: makeDisabled ? `var(--button-disabled-bg)` : `var(--button-${variant}-bg)`, 
      transform: 'none' 
    };
    buttonProps._hover = { 
      bg: makeDisabled ? `var(--button-disabled-bg)` : `var(--button-${variant}-bg)` 
    };
    buttonProps._focus = { 
      bg: makeDisabled ? `var(--button-disabled-bg)` : `var(--button-${variant}-bg)` 
    };
  }

  return (
    <Button {...buttonProps}>
      {variant === 'container' ? (
        <>
          <Text
            fontFamily={`var(--button-${effectiveVariant}-font-family, Sora)`}
            fontSize={`var(--button-${effectiveVariant}-font-size, 18px)`}
            fontWeight={`var(--button-${effectiveVariant}-font-weight, 700)`}
            lineHeight={`var(--button-${effectiveVariant}-line-height, 25.2px)`}
            textDecoration={textDecoration || `var(--button-${effectiveVariant}-text-decoration, none)`}
            textAlign="left"
            flex={1}
          >
            {label}
          </Text>
          {renderIcons([
            {
              name: 'FaChevronRight',
              size: 'lg',
              color: makeDisabled ? 'var(--button-disabled-icon-color, var(--primary-color))' : 'var(--primary-color)',
            },
          ])}
        </>
      ) : variant === 'modal' ? (
        <>
          <Box
            style={{
              position: 'relative',
              display: 'inline-flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {renderIcons(icons)}
          </Box>
          <Text
            fontFamily={`var(--${makeDisabled ? 'disabled-' : ''}${textVariant}-font-family, Sora)`}
            fontSize={`var(--${makeDisabled ? 'disabled-' : ''}${textVariant}-font-size, 18px)`}
            fontWeight={`var(--${makeDisabled ? 'disabled-' : ''}${textVariant}-font-weight, 700)`}
            lineHeight={`var(--${makeDisabled ? 'disabled-' : ''}${textVariant}-line-height, 25.2px)`}
            color={`var(--${makeDisabled ? 'disabled-' : ''}${textVariant}-color, var(--sutter-sync-purple))`}
          >
            {label || 'Need Help?'}
          </Text>
        </>
      ) : (
        <>
          {label ? (
            <Text
              fontFamily={`var(--button-${effectiveVariant}-font-family, Sora)`}
              fontSize={`var(--${makeDisabled ? 'disabled-' : ''}${effectiveTextVariant}-font-size, 18px)`}
              fontWeight={`var(--${makeDisabled ? 'disabled-' : ''}${effectiveTextVariant}-font-weight, 700)`}
              lineHeight={`var(--${makeDisabled ? 'disabled-' : ''}${effectiveTextVariant}-line-height, 25.2px)`}
              textDecoration={textDecoration || `var(--button-${effectiveVariant}-text-decoration, none)`}
            >
              {label}
            </Text>
          ) : (
            <VisuallyHidden>Button</VisuallyHidden>
          )}
        </>
      )}
    </Button>
  );
};

export default ButtonComponent;